import React, { useEffect, useState } from "react";
import { api, imageUrl } from "@/lib/api";
import { toast } from "sonner";
import { useMaterials } from "@/contexts/MaterialsContext";
import { useColours } from "@/contexts/ColoursContext";
import { X, Plus, Save, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import MaterialThumbnailPicker from "@/components/MaterialThumbnailPicker";

/**
 * Manager-only — admin-managed material families (Lunasoft, Spenco, EVA, etc.)
 * Each material can carry an optional palette: a subset of the global colours
 * that this material is available in. Used by StockFormDialog to filter the
 * colour dropdown.
 */
const ManageMaterialsDialog = ({ open, onClose }) => {
  const { reload } = useMaterials();
  const { colours: allColours } = useColours();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColours, setNewColours] = useState([]);
  const [newThumb, setNewThumb] = useState(null);
  const [newThick, setNewThick] = useState("");
  const [nameFocused, setNameFocused] = useState(false);

  const duplicate = (() => {
    const n = newName.trim().toLowerCase();
    if (!n) return null;
    return rows.find((r) => r.name.toLowerCase() === n) || null;
  })();
  const suggestions = (() => {
    const n = newName.trim().toLowerCase();
    if (!n || duplicate) return [];
    return rows
      .filter((r) => r.name.toLowerCase().includes(n))
      .slice(0, 6);
  })();

  const load = async () => {
    try {
      const { data } = await api.get("/materials");
      // Expose a plain-text edit buffer for the comma-separated thickness
      // input; canonical `thicknesses` is a number list.
      setRows(
        (data || []).map((m) => ({
          ...m,
          thicknessesRaw: (m.thicknesses || []).join(", "),
        })),
      );
    } catch (_) {
      toast.error("Could not load materials");
    }
  };

  // Parse "1, 2, 3.2" → [1, 2, 3.2] — drops non-numeric fragments so managers
  // can type freely without hitting validation errors.
  const parseThicknesses = (raw) => {
    if (!raw) return [];
    return String(raw)
      .split(/[,\s]+/)
      .map((t) => t.trim())
      .filter(Boolean)
      .map(Number)
      .filter((n) => Number.isFinite(n) && n > 0);
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  if (!open) return null;

  const create = async () => {
    const name = newName.trim();
    if (!name) return toast.error("Name required");
    setBusy(true);
    try {
      await api.post("/materials", {
        name,
        colours: newColours,
        thumbnail_image: newThumb,
        thicknesses: parseThicknesses(newThick),
      });
      setNewName("");
      setNewColours([]);
      setNewThumb(null);
      setNewThick("");
      await load();
      await reload();
      const tail = newColours.length
        ? ` with ${newColours.length} colour${newColours.length === 1 ? "" : "s"}`
        : "";
      toast.success(`Added material "${name}"${tail}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    } finally {
      setBusy(false);
    }
  };

  const save = async (row) => {
    setBusy(true);
    try {
      await api.put(`/materials/${row.id}`, {
        name: row.name,
        sort: row.sort,
        colours: row.colours || [],
        thumbnail_image: row.thumbnail_image || null,
        thicknesses: parseThicknesses(row.thicknessesRaw),
      });
      await reload();
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete material "${row.name}"? Items using it must be reassigned first.`)) return;
    setBusy(true);
    try {
      await api.delete(`/materials/${row.id}`);
      await load();
      await reload();
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const move = (idx, dir) => {
    const newRows = [...rows];
    const j = idx + dir;
    if (j < 0 || j >= newRows.length) return;
    [newRows[idx], newRows[j]] = [newRows[j], newRows[idx]];
    newRows.forEach((r, i) => (r.sort = i));
    setRows(newRows);
    // Persist both
    api.put(`/materials/${newRows[idx].id}`, { sort: idx }).catch(() => {});
    api.put(`/materials/${newRows[j].id}`, { sort: j }).then(reload).catch(() => {});
  };

  const update = (id, patch) =>
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="manage-materials-dialog"
      onClick={onClose}
    >
      <div
        className="nb-card w-[min(640px,94vw)] max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b-2 border-black flex items-center justify-between">
          <div>
            <div className="label-eyebrow">Inventory</div>
            <h3 className="font-display text-2xl">Manage materials</h3>
          </div>
          <button
            onClick={onClose}
            data-testid="manage-materials-close"
            className="nb-btn bg-white p-2"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-3">
          {rows.length === 0 && (
            <div className="text-sm text-[#607087] italic text-center py-4">
              No materials yet. Add Lunasoft, Spenco, EVA — whatever families you stock.
            </div>
          )}
          {rows.map((row, idx) => {
            const selected = Array.isArray(row.colours) ? row.colours : [];
            const toggle = (cname) => {
              const has = selected.includes(cname);
              update(row.id, {
                colours: has
                  ? selected.filter((x) => x !== cname)
                  : [...selected, cname],
              });
            };
            return (
              <div
                key={row.id}
                data-testid={`material-row-${row.name}`}
                className="border-2 border-black rounded-xl p-3 flex flex-col gap-3"
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                  <div className="flex gap-1">
                    <button
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      className="nb-btn bg-white p-1.5 disabled:opacity-30"
                      aria-label="move up"
                    >
                      <ArrowUp size={12} strokeWidth={2.5} />
                    </button>
                    <button
                      onClick={() => move(idx, 1)}
                      disabled={idx === rows.length - 1}
                      className="nb-btn bg-white p-1.5 disabled:opacity-30"
                      aria-label="move down"
                    >
                      <ArrowDown size={12} strokeWidth={2.5} />
                    </button>
                  </div>
                  <input
                    data-testid={`material-name-${row.name}`}
                    className="nb-input flex-1 min-w-[10rem]"
                    value={row.name}
                    onChange={(e) => update(row.id, { name: e.target.value })}
                  />
                  <input
                    data-testid={`material-thicknesses-${row.name}`}
                    className="nb-input w-full sm:w-48 font-mono-num"
                    value={row.thicknessesRaw ?? ""}
                    placeholder="mm, e.g. 1, 2, 3, 6"
                    onChange={(e) =>
                      update(row.id, { thicknessesRaw: e.target.value })
                    }
                    title="Comma-separated thicknesses in mm"
                  />
                  <MaterialThumbnailPicker
                    value={row.thumbnail_image}
                    onChange={(v) => update(row.id, { thumbnail_image: v })}
                    busy={busy}
                    testidName={row.name}
                  />
                  <div className="flex gap-2">
                    <button
                      data-testid={`material-save-${row.name}`}
                      onClick={() => save(row)}
                      disabled={busy}
                      className="nb-btn nb-btn-primary px-3 py-2 text-sm flex items-center gap-1"
                    >
                      <Save size={14} strokeWidth={2.5} /> Save
                    </button>
                    <button
                      data-testid={`material-delete-${row.name}`}
                      onClick={() => remove(row)}
                      disabled={busy}
                      className="nb-btn nb-btn-danger p-2"
                      aria-label="delete"
                    >
                      <Trash2 size={14} strokeWidth={2.5} />
                    </button>
                  </div>
                </div>
                {allColours.length > 0 && (
                  <div className="flex flex-col gap-1.5 pl-1">
                    <div className="label-eyebrow text-[#607087]">
                      Available colours {selected.length === 0 && "(none = any)"}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {allColours.map((c) => {
                        const active = selected.includes(c.name);
                        return (
                          <button
                            key={c.id}
                            type="button"
                            data-testid={`material-${row.name}-palette-${c.name}`}
                            onClick={() => toggle(c.name)}
                            className={`nb-btn px-2.5 py-1 text-xs flex items-center gap-1 ${
                              active ? "nb-btn-dark" : "bg-white"
                            }`}
                          >
                            {c.swatch && (
                              <span
                                className="inline-block w-2.5 h-2.5 rounded-full border border-black/40"
                                style={{ background: c.swatch }}
                              />
                            )}
                            {c.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <div className="border-2 border-dashed border-[#001740] rounded-xl p-3 flex flex-col gap-3 bg-[#F4F8FB]">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3">
              <label className="flex-1 relative">
                <div className="label-eyebrow mb-1">New material</div>
                <input
                  data-testid="new-material-name"
                  className="nb-input"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onFocus={() => setNameFocused(true)}
                  onBlur={() => setTimeout(() => setNameFocused(false), 150)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && !duplicate && newName.trim() && create()
                  }
                  placeholder="e.g. Lunasoft, Spenco, EVA, Leather"
                  autoComplete="off"
                />
                {nameFocused && suggestions.length > 0 && (
                  <div
                    data-testid="new-material-suggestions"
                    className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-black rounded-md shadow-lg z-20 max-h-44 overflow-y-auto"
                  >
                    {suggestions.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        data-testid={`new-material-suggest-${s.name}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setNewName(s.name);
                          setNameFocused(false);
                          toast.info(`"${s.name}" already exists — edit its row above`);
                        }}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-[#F1E7FA] border-b border-black/10 last:border-b-0"
                      >
                        {s.name}
                      </button>
                    ))}
                  </div>
                )}
              </label>
              <label className="sm:w-52">
                <div className="label-eyebrow mb-1">Thicknesses (mm)</div>
                <input
                  data-testid="new-material-thicknesses"
                  className="nb-input font-mono-num"
                  value={newThick}
                  onChange={(e) => setNewThick(e.target.value)}
                  placeholder="e.g. 1, 2, 3, 6, 9, 12"
                  autoComplete="off"
                />
              </label>
              <button
                data-testid="new-material-add"
                onClick={create}
                disabled={busy || !newName.trim() || !!duplicate}
                className="nb-btn nb-btn-success px-4 py-2 text-sm flex items-center gap-1"
              >
                <Plus size={14} strokeWidth={2.5} /> Add
              </button>
            </div>

            {duplicate && (
              <div
                data-testid="new-material-duplicate-warning"
                className="text-xs font-semibold px-3 py-2 rounded-md border-2 border-[#C7A43B] bg-[#FFF7DB] text-[#5B4500]"
              >
                {`"${duplicate.name}" already exists — edit its row above instead of creating a duplicate.`}
              </div>
            )}

            {allColours.length > 0 && !duplicate && (
              <div className="flex flex-col gap-1.5">
                <div className="label-eyebrow text-[#607087]">
                  Available colours for this material {newColours.length === 0 && "(none = any)"}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {allColours.map((c) => {
                    const active = newColours.includes(c.name);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        data-testid={`new-material-palette-${c.name}`}
                        onClick={() =>
                          setNewColours((cs) =>
                            active ? cs.filter((x) => x !== c.name) : [...cs, c.name],
                          )
                        }
                        className={`nb-btn px-2.5 py-1 text-xs flex items-center gap-1 ${
                          active ? "nb-btn-dark" : "bg-white"
                        }`}
                      >
                        {c.swatch_image ? (
                          <img
                            src={imageUrl(c.swatch_image)}
                            alt=""
                            className="inline-block w-2.5 h-2.5 rounded-full border border-black/40 object-cover"
                          />
                        ) : c.swatch ? (
                          <span
                            className="inline-block w-2.5 h-2.5 rounded-full border border-black/40"
                            style={{ background: c.swatch }}
                          />
                        ) : null}
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {!duplicate && (
              <div className="flex flex-col gap-1.5">
                <div className="label-eyebrow text-[#607087]">Thumbnail (optional)</div>
                <MaterialThumbnailPicker
                  value={newThumb}
                  onChange={setNewThumb}
                  busy={busy}
                  testidName="new"
                />
              </div>
            )}
          </div>

          <div className="label-eyebrow text-[#607087] mt-1">
            Materials can be renamed — existing stock items will follow.
            Delete is blocked while items still reference a material.
            Tap colour chips to limit which colours appear in the item form
            when this material is chosen. Empty = any colour allowed.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageMaterialsDialog;
